import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { AuthService } from "../services/auth.service";
import { Pass } from "../../administration/models/pass.model";
import { SnackService } from "../../../shared/services/snack-service.service";
import { LocalStorageService } from "../../../shared/services/local-storage.service";
import { MatDialog } from "@angular/material/dialog";
import { DialogsService } from "../../../shared/services/dialogs/dialogs.service";
import { firstValueFrom } from "rxjs";
import { PassFacadeService } from "../../administration/services/pass-store/pass-facade.service";

@Injectable({
  providedIn: "root",
})
export class RouteGuard implements CanActivate {
  dialogService = inject(DialogsService);
  passFacade = inject(PassFacadeService);

  constructor(
    public authService: AuthService,
    private snackService: SnackService,
    private localStorageService: LocalStorageService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    // On n'active pas une route protégée avant que ses données existent.
    //
    // L'application POSTULE que les `ArticleInfos` sont là : 166 fichiers
    // injectent leur facade, 427 lectures dans le code. Rien ne les attendait —
    // `HomeModule` lance `loadStoredUser()` sans `await` — si bien que les
    // écrans s'ouvraient pendant que le catalogue (6,6 Mo) arrivait, et
    // rendaient des listes vides que rien ne distinguait d'« aucun résultat »
    // (realm#74). ~100 ms en local, davantage sur une connexion lente : c'est
    // le prix d'un écran qui ne ment pas.
    //
    // Idempotent (la promesse est mutualisée), immédiat pour un visiteur
    // anonyme, et ne rejette jamais — un back dégradé rendrait sinon
    // l'application inaccessible, ce qui serait pire que le défaut corrigé.
    await this.authService.attendreDonneesInitiales();

    // Soft logout = session EXISTANTE devenue inactive → dialog de
    // re-authentification (l'email de l'utilisateur y est pré-rempli).
    //
    // La condition `userSignal()` est indispensable : `isSoftLogout()` vaut
    // `!userActive()`, et `userActive` démarre à `false`. Un visiteur ANONYME
    // (aucune session en localStorage, donc aucune hydratation au boot) tombait
    // donc dans cette branche, qui retourne `true` : la route protégée était
    // ACTIVÉE (composant instancié, requêtes parties) avec, par-dessus, un
    // dialog de « re-connexion » sans email à ré-entrer. Sans session, le seul
    // traitement correct est la redirection vers /auth/login, plus bas.
    // ⚠️ Le `dashboard` est EXEMPTÉ, et il doit le rester.
    //
    // `isSoftLogout()` vaut `!userActive()`, et `userActive` démarre à `false` :
    // la condition est donc vraie à CHAQUE démarrage, avant même que la session
    // soit marquée active. Or le dashboard est la première route activée après
    // connexion — la seule à traverser cette fenêtre. Sans cette exemption,
    // `handleSoftLogout()` ouvre un dialogue bloquant à chaque boot et ATTEND
    // sa fermeture avant d'activer la route (mesuré le 20/08/2026 en retirant
    // l'exemption : 129 specs du catalogue bloqués).
    //
    // L'exemption a donc une raison, et elle est bonne. Ce qui manque est
    // ailleurs : sur cette route, personne ne traite l'inactivité RÉELLE —
    // voir `HomeComponent.checkInactivityOnInit`.
    if (
      this.authService.isSoftLogout() &&
      this.authService.userSignal() &&
      route.url?.[0]?.path !== "dashboard"
    ) {
      return this.handleSoftLogout();
    }
    const dialogs = this.dialog.openDialogs;
    dialogs.forEach((d) => d.close());
    const pass = this.passFacade.currentPass();

    if (!pass) {
      if (this.authService.userSignal()) {
        return this.handleAccess(
          this.localStorageService.getWithTTL("PASS"),
          state.url,
        );
      } else {
        this.router.navigateByUrl("/auth/login");
        return false;
      }
    } else {
      return this.handleAccess(pass, state.url);
    }
  }

  handleAccess(pass: Pass, url: string) {
    let canRoute = true;

    if (!pass) {
      this.localStorageService.putWithTTL("lastUrl", this.router.url, 1000);
      this.router.navigateByUrl("/auth/login");
      return false;
    }

    if (!pass.tiers && url.includes("tiers")) {
      canRoute = false;
    }

    if (
      !pass.specifs &&
      (url.includes("articles") || url.includes("specification"))
    ) {
      canRoute = false;
    }

    if (!pass.infos_journalieres && url.includes("info-journalieres")) {
      canRoute = false;
    }

    if (!pass.lignes && url.includes("lignes")) {
      canRoute = false;
    }

    if (!pass.taches && url.includes("lists")) {
      canRoute = false;
    }

    if (!pass.stocks && (url.includes("stock") || url.includes("lots"))) {
      canRoute = false;
    }

    if (
      !pass.plannif_prod &&
      (url.includes("of-plannif") || url.includes("article-planification"))
    ) {
      canRoute = false;
    }

    if (!pass.stats_prod && url.includes("stats")) {
      canRoute = false;
    }

    if (
      !pass.commandes &&
      (url.includes("avoir-fournisseur") ||
        url.includes("commandes") ||
        url.includes("facture-fournisseur"))
    ) {
      canRoute = false;
    }

    if (
      !pass.commandesClient &&
      (url.includes("avoir") ||
        url.includes("commandeclient") ||
        url.includes("facture") ||
        url.includes("expeditions"))
    ) {
      canRoute = false;
    }

    if (!canRoute) {
      this.snackService.info(
        "Vous n'avez pas les droits pour accéder à cette page",
      );
      return false;
    } else {
      return true;
    }
  }

  private async handleSoftLogout() {
    const dialogRef = this.authService.askLogin();
    await firstValueFrom(dialogRef.afterClosed());
    return true;
  }
}
